import { useState, FormEvent } from "react";
import InnerPageLayout from "../../components/feature/InnerPageLayout";

const partnerTypes = [
  {
    icon: "ri-building-4-line",
    color: "#A855F7",
    glow: "rgba(168,85,247,0.12)",
    title: "에이전시 · 기획사",
    desc: "소속 아티스트의 IP를 체계적으로 비즈니스화하고 싶은 에이전시와 협력합니다. 다수 아티스트 번들 계약도 가능합니다.",
  },
  {
    icon: "ri-store-3-line",
    color: "#14F1D9",
    glow: "rgba(20,241,217,0.12)",
    title: "브랜드 · 기업 콜라보",
    desc: "아르트레너 파트너 아티스트의 IP를 활용한 콜라보레이션 굿즈, 캠페인, 공간 디자인을 원하는 기업과 연결합니다.",
  },
  {
    icon: "ri-funds-line",
    color: "#F97316",
    glow: "rgba(249,115,22,0.12)",
    title: "투자사 · 엑셀러레이터",
    desc: "아르트레너가 인큐베이팅 중인 아티스트 브랜드에 투자하거나, 공동 포트폴리오를 구성하고 싶은 투자자를 환영합니다.",
  },
];

export default function PartnershipPage() {
  const [form, setForm] = useState({ name: "", company: "", email: "", phone: "", type: "", message: "" });
  const [status, setStatus] = useState<"idle" | "loading" | "success" | "error">("idle");

  const handleChange = (e: React.ChangeEvent<HTMLInputElement | HTMLTextAreaElement | HTMLSelectElement>) => {
    setForm((f) => ({ ...f, [e.target.name]: e.target.value }));
  };

  const handleSubmit = async (e: FormEvent<HTMLFormElement>) => {
    e.preventDefault();
    if (!form.name || !form.email || !form.message) return;
    setStatus("loading");
    try {
      const params = new URLSearchParams();
      Object.entries(form).forEach(([k, v]) => params.append(k, v));
      const res = await fetch("https://readdy.ai/api/form/d72g3h4ij602egd95h7g", {
        method: "POST",
        headers: { "Content-Type": "application/x-www-form-urlencoded" },
        body: params.toString(),
      });
      setStatus(res.ok ? "success" : "error");
    } catch {
      setStatus("error");
    }
  };

  const inputCls = "w-full px-5 py-3.5 rounded-2xl text-sm font-body text-white placeholder-white/30 outline-none transition-all duration-300 border focus:border-brand-purple/60";
  const inputStyle = { background: "rgba(255,255,255,0.04)", borderColor: "rgba(255,255,255,0.1)" };

  return (
    <InnerPageLayout>
      {/* Hero */}
      <section className="relative py-24 lg:py-40 px-4 md:px-6 overflow-hidden">
        <div className="absolute inset-0 pointer-events-none" style={{ background: "radial-gradient(ellipse at 50% 40%, rgba(249,115,22,0.08) 0%, transparent 60%)" }} />
        <div className="max-w-7xl mx-auto text-center">
          <span className="inline-block text-xs font-display font-medium tracking-widest uppercase text-brand-orange mb-6 px-3 py-1 rounded-full border border-brand-orange/30 bg-brand-orange/5">
            Partnership
          </span>
          <h1 className="font-display text-4xl md:text-6xl lg:text-7xl font-bold text-white leading-snug lg:leading-relaxed mb-7">
            함께 더 큰 그림을<br />
            <span className="gradient-text-brand">그려봅시다.</span>
          </h1>
          <p className="text-base md:text-lg text-white/60 font-body leading-loose max-w-2xl mx-auto">
            아르트레너는 에이전시, 브랜드, 투자사와의 전략적 파트너십을 통해 크리에이터 이코노미의 새로운 지평을 열어갑니다.
          </p>
        </div>
      </section>

      {/* Partner Types */}
      <section className="py-20 lg:py-28 px-4 md:px-6" style={{ background: "linear-gradient(180deg, #06080F 0%, #0A0B16 100%)" }}>
        <div className="max-w-7xl mx-auto">
          <div className="text-center mb-14 lg:mb-18">
            <h2 className="font-display text-3xl md:text-4xl font-bold text-white leading-snug">
              어떤 파트너십을 원하시나요?
            </h2>
          </div>
          <div className="grid grid-cols-1 md:grid-cols-3 gap-6 lg:gap-8">
            {partnerTypes.map((pt) => (
              <div key={pt.title} className="glass rounded-2xl p-7 lg:p-9 flex flex-col gap-6" style={{ border: `1px solid ${pt.color}20` }}>
                <div className="w-12 h-12 flex items-center justify-center rounded-xl" style={{ background: pt.glow, border: `1px solid ${pt.color}30` }}>
                  <i className={`${pt.icon} text-xl`} style={{ color: pt.color }} />
                </div>
                <div>
                  <h3 className="font-display text-lg font-bold text-white mb-3">{pt.title}</h3>
                  <p className="text-white/50 text-sm font-body leading-loose">{pt.desc}</p>
                </div>
              </div>
            ))}
          </div>
        </div>
      </section>

      {/* Form */}
      <section className="py-20 lg:py-32 px-4 md:px-6 bg-brand-bg">
        <div className="max-w-2xl mx-auto">
          <div className="text-center mb-12 lg:mb-16">
            <span className="inline-block text-xs font-display font-medium tracking-widest uppercase text-brand-purple mb-5 px-3 py-1 rounded-full border border-brand-purple/30 bg-brand-purple/5">
              Contact Us
            </span>
            <h2 className="font-display text-3xl md:text-4xl font-bold text-white leading-snug">
              파트너십 문의하기
            </h2>
            <p className="mt-5 text-white/50 font-body text-sm leading-relaxed">
              문의 주시면 영업일 기준 2일 이내 담당자가 연락드립니다.
            </p>
          </div>

          {status === "success" ? (
            <div className="glass rounded-3xl p-10 text-center">
              <div className="w-16 h-16 rounded-full mx-auto mb-6 flex items-center justify-center" style={{ background: "rgba(20,241,217,0.15)", border: "1px solid rgba(20,241,217,0.3)" }}>
                <i className="ri-check-double-line text-brand-teal text-2xl" />
              </div>
              <h3 className="font-display text-xl font-bold text-white mb-4">문의가 접수됐습니다!</h3>
              <p className="text-white/60 font-body text-sm leading-loose">영업일 기준 2일 이내에 담당자가 연락드릴게요.</p>
            </div>
          ) : (
            <form
              data-readdy-form
              id="partnership-inquiry"
              onSubmit={handleSubmit}
              className="glass rounded-3xl p-7 lg:p-10 flex flex-col gap-5"
            >
              <div className="grid grid-cols-1 sm:grid-cols-2 gap-4">
                <div className="flex flex-col gap-2">
                  <label className="text-xs font-display font-semibold text-white/50 uppercase tracking-wider">담당자 성함 *</label>
                  <input name="name" required value={form.name} onChange={handleChange} placeholder="홍길동" className={inputCls} style={inputStyle} />
                </div>
                <div className="flex flex-col gap-2">
                  <label className="text-xs font-display font-semibold text-white/50 uppercase tracking-wider">소속 기관명</label>
                  <input name="company" value={form.company} onChange={handleChange} placeholder="(주)아트컴퍼니" className={inputCls} style={inputStyle} />
                </div>
              </div>
              <div className="grid grid-cols-1 sm:grid-cols-2 gap-4">
                <div className="flex flex-col gap-2">
                  <label className="text-xs font-display font-semibold text-white/50 uppercase tracking-wider">이메일 *</label>
                  <input type="email" name="email" required value={form.email} onChange={handleChange} placeholder="contact@company.com" className={inputCls} style={inputStyle} />
                </div>
                <div className="flex flex-col gap-2">
                  <label className="text-xs font-display font-semibold text-white/50 uppercase tracking-wider">연락처</label>
                  <input name="phone" value={form.phone} onChange={handleChange} placeholder="010-0000-0000" className={inputCls} style={inputStyle} />
                </div>
              </div>
              <div className="flex flex-col gap-2">
                <label className="text-xs font-display font-semibold text-white/50 uppercase tracking-wider">파트너십 유형</label>
                <select name="type" value={form.type} onChange={handleChange} className={inputCls} style={{ ...inputStyle, appearance: "none" }}>
                  <option value="" style={{ background: "#06080F" }}>유형을 선택해주세요</option>
                  <option value="agency" style={{ background: "#06080F" }}>에이전시 · 기획사</option>
                  <option value="brand" style={{ background: "#06080F" }}>브랜드 · 기업 콜라보</option>
                  <option value="investor" style={{ background: "#06080F" }}>투자사 · 엑셀러레이터</option>
                  <option value="other" style={{ background: "#06080F" }}>기타 문의</option>
                </select>
              </div>
              <div className="flex flex-col gap-2">
                <label className="text-xs font-display font-semibold text-white/50 uppercase tracking-wider">문의 내용 * <span className="text-white/20 font-body font-normal normal-case">(최대 500자)</span></label>
                <textarea
                  name="message"
                  required
                  value={form.message}
                  onChange={(e) => e.target.value.length <= 500 && handleChange(e)}
                  placeholder="파트너십 목적, 원하시는 협력 방식, 예상 규모 등을 자유롭게 적어주세요."
                  rows={5}
                  className={`${inputCls} resize-none leading-loose`}
                  style={inputStyle}
                />
                <span className="text-xs text-white/25 text-right">{form.message.length}/500</span>
              </div>

              {status === "error" && (
                <p className="text-sm text-red-400 font-body">오류가 발생했어요. 잠시 후 다시 시도해주세요.</p>
              )}

              <button
                type="submit"
                disabled={status === "loading"}
                className="w-full flex items-center justify-center gap-2 py-4 rounded-2xl font-display font-semibold text-white cursor-pointer transition-all duration-300 disabled:opacity-50 whitespace-nowrap"
                style={{ background: "linear-gradient(135deg, #A855F7, #7C3AED)", boxShadow: "0 0 20px rgba(168,85,247,0.3)" }}
              >
                {status === "loading" ? (
                  <><i className="ri-loader-4-line animate-spin" /> 전송 중...</>
                ) : (
                  <><i className="ri-send-plane-line" /> 파트너십 문의 보내기</>
                )}
              </button>
            </form>
          )}
        </div>
      </section>
    </InnerPageLayout>
  );
}
